import animate from '@play-co/timestep-core/lib/animate';
import { playExtraMovesOfferAnimation } from '../movesPinch/extraMovesOffer.animation';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import { animDuration, delay } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import Match2SocialContinueMovesPanel from './Match2SocialContinueMovesPanel';
import Match2SocialContinueBuyPanel from './Match2SocialContinueBuyPanel';
import { maxSocialContinueRewardMoves } from 'src/replicant/social/social.ruleset';
import { startInviteFriendsSequence } from 'src/game/social/social.sequence';
import { getMatch2ContinueConfig } from 'src/replicant/match2/match2.getters';
import { user } from 'src/StateObserver';
import { GemsWallet } from 'src/game/components/GemsWallet';
import { movesPinchCheck } from 'src/lib/pinch';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: { invites?: number; purchase?: boolean }) => void;

const ui = uiConfig.buruburu.care.confirmation;

export default class PopupMatch2SocialContinue extends PopupBasic {
  private contentWrapper: View;
  private movesPanel: Match2SocialContinueMovesPanel;
  /** Gem offer panel; only its move quote varies for eligible retries. */
  private buyPanel: Match2SocialContinueBuyPanel;
  private gemsWallet: GemsWallet;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private isPopupHidden = false;
  /** Regular amount displayed by the buy panel before the bonus reveal. */
  private readonly regularMoves: number;
  /** Bonus passed to init, revealed after the popup opens. */
  private extraMoves = 0;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const offset = 150;
    super({
      superview: opts.superview,
      close: () => opts.close({ purchase: false }),
      ...ui.popup,
      width: 630,
      height: ui.popup.height - offset + 20,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.SOCIAL_CONTINUE.POPUP,
      },
    });

    this.message.removeFromSuperview();
    this.title.updateOpts({ size: 30 });
    this.bg.onClick = () => this.toggleShowPuzzle();

    this.gemsWallet = new GemsWallet({
      superview: this.root,
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    this.movesPanel = new Match2SocialContinueMovesPanel({
      superview: this.contentWrapper,
      x: 465,
      y: 205,
    });

    const state = user();
    const { moves, price } = getMatch2ContinueConfig(state);
    this.regularMoves = moves;
    this.buyPanel = new Match2SocialContinueBuyPanel({
      superview: this.contentWrapper,
      amount: moves,
      continues: state.match2.continues,
      x: 165,
      y: 205,
    });

    // Buy button
    new ButtonScaleViewWithText({
      ...ui.okButton,
      x: 40,
      y: ui.okButton.y - offset,
      width: 265,
      superview: this.contentWrapper,
      labelOffsetX: -30,
      icon: {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 47,
        height: 40,
        offset: {
          x: 90,
          y: 0,
        },
      },
      localeText: () => `${i18n('basic.buy')} ${price}`,
      onClick: async () => {
        sounds.sfx.play('ok');
        const success = await movesPinchCheck(price, 'match2');
        if (success) opts.close({ purchase: true });
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.SOCIAL_CONTINUE.POPUP_CLOSE,
      },
    });

    // Invite button
    new ButtonScaleViewWithText({
      ...ui.okButton,
      x: 326,
      y: ui.okButton.y - offset,
      width: 265,
      superview: this.contentWrapper,
      localeText: () => i18n('match2.socialContinue.inviteCTA'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.SOCIAL_CONTINUE.POPUP_INVITE,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        const friends = await startInviteFriendsSequence(
          Feature.CONTINUE._,
          Feature.CONTINUE.SOCIAL,
          'moves',
          maxSocialContinueRewardMoves,
        );
        if (friends?.length) opts.close({ invites: friends.length });
      },
    });

    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 100 + this.box.style.y + this.box.style.height * 0.5 + 100,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/hud/icons/hud_gem.png',
      // Preload the supplied sparkle before the offer animation starts.
      'assets/ui/match2/movepinch/sparkle.png',
    ]);
  }

  public init({ extraMoves = 0 }: { extraMoves?: number }) {
    super.init({ title: i18n('match2.socialContinue.title') });

    // Retain the bonus for the opening animation.
    this.extraMoves = extraMoves;

    this.movesPanel.init(maxSocialContinueRewardMoves);
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;

    // Toggle CTAs interactivity accordingly
    this.box.updateOpts({ blockEvents: this.isPopupHidden });

    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    // Wait for the opening tween before taking over its input blocker.
    await super.fadeIn();
    await animate(this.box).toPromise();
    this.overlay.show();
    await this.tweenToggleMessageScaleY(1);

    await playExtraMovesOfferAnimation({
      badge: this.buyPanel.movesBadge,
      label: this.buyPanel.movesLabel,
      regularMoves: this.regularMoves,
      setNumMoves: (moves) => this.buyPanel.setNumMoves(moves),
      bonusMoves: this.extraMoves,
    });
    this.overlay.hide();
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
    await delay(animDuration * 2);
  }
}
